import { create } from 'zustand';

import { Benefit, BenefitCategory, BenefitLevel } from 'src/types/benefits/benefit';
import { Option } from 'src/types/common/option';

type States = {
  isLoading: boolean;
  benefits: Benefit[];
  categories: BenefitCategory[];
  levels: BenefitLevel[];
  categoriesOptions: Option[];
  levelsOptions: Option[];
  storesOptions: Option[];
  eventsOptions: Option[];
  checkedIds: string[];
  openForm: boolean;
  selectedBenefit: Benefit | null;
};

type Actions = {
  setBenefits: (benefits: Benefit[]) => void;
  setCategories: (categories: BenefitCategory[]) => void;
  setLevels: (levels: BenefitLevel[]) => void;
  setCategoriesOptions: (categories: BenefitCategory[]) => void;
  setLevelsOptions: (levels: BenefitLevel[]) => void;
  setStoresOptions: (stores: Option[]) => void;
  setEventsOptions: (events: Option[]) => void;
  setLoading: (isLoading: boolean) => void;
  setCheckedIds: (ids: string[]) => void;
  setOpenForm: (open: boolean) => void;
  setSelectedBenefit: (benefit: Benefit | null) => void;
  toggleBenefitStatus: (id: string) => void;
  addBenefit: (benefit: Benefit) => void;
  updateBenefit: (benefit: Benefit) => void;
  deleteBenefit: (id: string) => void;
};

type BenefitsStore = Actions & States;

const initialValues = {
  isLoading: false,
  benefits: [],
  categories: [],
  levels: [],
  categoriesOptions: [],
  levelsOptions: [],
  storesOptions: [],
  eventsOptions: [],
  checkedIds: [],
  openForm: false,
  selectedBenefit: null,
};

export const useBenefitsStore = create<BenefitsStore>((set: any, get: any) => ({
  ...initialValues,

  setBenefits: (benefits: Benefit[]) => set({ benefits }),

  setCategories: (categories: BenefitCategory[]) => set({ categories }),

  setLevels: (levels: BenefitLevel[]) => set({ levels }),

  setCategoriesOptions: (categories: BenefitCategory[]) =>
    set({ categoriesOptions: categories.map((cat) => ({ label: cat.name, value: cat.id })) }),

  setLevelsOptions: (levels: BenefitLevel[]) => set({ levelsOptions: levels.map((level) => ({ label: level.name, value: level.id })) }),

  setStoresOptions: (stores: Option[]) => set({ storesOptions: stores }),

  setEventsOptions: (events: Option[]) => set({ eventsOptions: events }),

  setLoading: (isLoading: boolean) => set({ isLoading }),

  setCheckedIds: (ids: string[]) => set({ checkedIds: ids }),

  setOpenForm: (open: boolean) => set({ openForm: open }),

  setSelectedBenefit: (benefit: Benefit | null) => set({ selectedBenefit: benefit }),

  toggleBenefitStatus: (id: string) => {
    const { benefits } = get();
    const updatedBenefits = benefits.map((benefit: Benefit) =>
      benefit._id === id ? { ...benefit, isActive: !benefit.isActive } : benefit
    );
    set({ benefits: updatedBenefits });
  },

  addBenefit: (benefit: Benefit) => {
    const { benefits } = get();
    set({ benefits: [...benefits, benefit] });
  },

  updateBenefit: (updatedBenefit: Benefit) => {
    const { benefits } = get();
    const updatedBenefits = benefits.map((benefit: Benefit) => (benefit._id === updatedBenefit._id ? updatedBenefit : benefit));
    set({ benefits: updatedBenefits });
  },

  deleteBenefit: (id: string) => {
    const { benefits } = get();
    const updatedBenefits = benefits.filter((benefit: Benefit) => benefit._id !== id);
    set({ benefits: updatedBenefits });
  },
}));
